import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { AppConfigModel } from 'src/app/core/api/app-config.service';
import { RoleEnum } from 'src/app/shared/constants/role.enums';
import { updateAppConfig } from 'src/app/store/app-config/app-config.actions';
import { selectAppConfigState } from 'src/app/store/app-config/app-config.selectors';
import { selectCurrentUser } from 'src/app/store/auth/auth.selectors';

import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import * as fromApp from '../../../store';

@Component({
  selector: 'fc-edit-app-config-page',
  templateUrl: './edit-app-config-page.component.html',
  styleUrls: ['./edit-app-config-page.component.scss'],
})
export class EditAppConfigPageComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  id: number = null;
  appConfig: AppConfigModel = null;
  appConfigForm: UntypedFormGroup;
  doNavigation: boolean = false;
  error: string = null;
  isSuperAdmin: boolean = false;
  origValue: string = null;

  constructor(
    readonly store: Store<fromApp.AppState>,
    private formBuilder: UntypedFormBuilder,
    readonly router: Router,
    public dialog: MatDialog,
    readonly route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.id = +this.route.snapshot.params.id;

    this.store
      .select(selectAppConfigState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        if (this.doNavigation) {
          this.doNavigation = false;
          this.router.navigateByUrl('/admin/manage/app-config');
        } else if (state.error) {
          this.error = state.error ? `Error: ${state.error}` : null;
        } else if (state.appConfig) {
          this.appConfig = state.appConfig.find(ac => ac.id === this.id);
          if (this.appConfig) {
            this.origValue = this.appConfig.value;
            this.appConfigForm = this.formBuilder.group({
              id: this.appConfig.id,
              key: [this.appConfig.key, Validators.required],
              value: [this.appConfig.value, Validators.required],
              description: [this.appConfig.description, Validators.required],
            });
          }
        }
      });
    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.isSuperAdmin = currentUser.hasRole(RoleEnum.SuperAdmin);
      });
  }

  onSubmit(): void {
    const line1 = `This will update ${this.appConfig.key}`;
    const line2 = `Old value:${this.appConfig.value}`;
    const line3 = `New value:${this.appConfigForm.value.value}`;
    const dialogRef = this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Are You Sure?',
        line1,
        line2,
        line3,
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(confirm => {
      if (confirm) {
        this.store.dispatch(
          updateAppConfig({
            appConfig: this.appConfigForm.value,
          })
        );
        this.appConfigForm.markAsPristine();
        this.doNavigation = true;
      }
    });
  }

  get allowSave(): boolean {
    const value = (this.appConfigForm.value.value || '').trim();
    const changed = this.origValue !== value;
    return this.isSuperAdmin && changed;
  }

  get isFormDirty(): boolean {
    return this.appConfigForm.dirty;
  }
}
